import { Flex, Text } from "@mantine/core";
import { BarDatum, ResponsiveBar } from "@nivo/bar";
import { useState, useCallback } from "react";
// prettier-ignore
const keys = ["Empresas inscritas por dia"];

const MAGIC_NUMBER = 1.6;

// prettier-ignore
const AreaRelatorio = ({ empresas, onDateSelect }: { 
  empresas: TConvite[];
  onDateSelect?: (date: string | null) => void;
}) => {
  const [selectedBar, setSelectedBar] = useState<string | null>(null);
  const formatData = formatArray(empresas);

  interface BarColorDatum {
    data?: { country?: string | number };
    indexValue?: string | number;
  }
  
  const getBarColor = useCallback((datum: BarColorDatum) => {
    const dateKey = String(datum.data?.country || datum.indexValue || "");
    return selectedBar === dateKey ? "#ff6b35" : "#61cdbb";
  }, [selectedBar]);


  const handleBarClick = useCallback((data: { data: BarDatum & { originalDate?: string } }) => {
    const dateKey = String(data.data.country);
    // Usa originalDate se disponível, senão converte DD-MM-YY de volta para DD/MM/YYYY
    const originalDate = data.data.originalDate;
    let dateForFilter: string;
    
    if (originalDate) {
      dateForFilter = originalDate;
    } else {
      // Converte DD-MM-YY de volta para DD/MM/YYYY
      const [day, month, shortYear] = dateKey.split('-');
      const fullYear = `20${shortYear}`; // Assume século 21
      dateForFilter = `${day}/${month}/${fullYear}`;
    }
    
    // Toggle: se já está selecionada, remove o filtro; senão, aplica o filtro
    if (selectedBar === dateKey) {
      setSelectedBar(null);
      if (onDateSelect) {
        onDateSelect(null);
      }
    } else {
      setSelectedBar(dateKey);
      if (onDateSelect) {
        onDateSelect(dateForFilter);
      }
    }
  }, [selectedBar, onDateSelect]);

  return <>
    <Text size={"md"} fw={700}>Empresas inscritas por dia</Text>
    <Flex h={400} justify="center" style={{ position: "relative" }}>
      <ResponsiveBar
        data={formatData}
        keys={keys}
        indexBy="country"
        margin={{ top: 50, right: 130, bottom: 80, left: 60 }}
        padding={0.3}
        colorBy="indexValue"
        valueScale={{ type: "linear" }}
        indexScale={{ type: "band", round: true }}
        borderColor={{
          from: "color",
          modifiers: [
            [
              "darker",
              MAGIC_NUMBER
            ]
          ]
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: -90
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{
          from: "color",
          modifiers: [
            [
              "darker",
              MAGIC_NUMBER
            ]
          ]
        }}
        role="application"
        ariaLabel="Nivo bar chart demo"
        onClick={handleBarClick}
        colors={getBarColor}
      // barAriaLabel={e => e.id + ": " + e.formattedValue + " in country: " + e.indexValue}
      />
    </Flex>
  </>

};

export default AreaRelatorio;

/**
 * Formata um array de convites em um array de dados compatíveis com o tipo BarDatum.
 * @param originalArray originalArray O array de convites original.
 * @returns O array de dados formatado.
 */
// prettier-ignore
function formatArray(originalArray: TConvite[]): BarDatum[] {
  // Objeto para armazenar a contagem de empresas por data
  // eslint-disable-next-line unicorn/no-array-reduce
  const dateCountMap: Record<string, number> = originalArray.reduce((accumulator: Record<string, number>, object: TConvite) => {
    // Extrai a data do campo createdAt e formata como DD/MM/YYYY
    const date = new Date(object.createdAt);
    const formattedDate: string = `${String(date.getDate()).padStart(PAD_NUMBER, '0')}/${String(date.getMonth() + 1).padStart(PAD_NUMBER, '0')}/${date.getFullYear()}`;

    // Se a data não estiver no acumulador, inicializa com 0
    const currentCount = accumulator[formattedDate] ?? 0;
    // eslint-disable-next-line security/detect-object-injection
    accumulator[formattedDate] = currentCount + 1;

    return accumulator;
  }, {} as Record<string, number>);

  // Constrói o array resultante com a contagem de empresas por dia
  const result: BarDatum[] = Object.entries(dateCountMap).map(([date, count]) => {
    // Converte DD/MM/YYYY para DD-MM-YY
    const [day, month, year] = date.split('/');
    const YEAR_SUFFIX_LENGTH = 2;
    const shortYear = year?.slice(-YEAR_SUFFIX_LENGTH) || '00';
    const formattedLabel = `${day}-${month}-${shortYear}`;
    
    return {
      "country": formattedLabel,
      "Empresas inscritas por dia": count,
      originalDate: date // Mantém a data original para filtros
    } as BarDatum & { originalDate: string };
  });

  // Ordena o resultado por data (mais antiga para mais recente)
  result.sort((a, b) => {
    // Usa originalDate para ordenação
    const itemA = a as BarDatum & { originalDate?: string };
    const itemB = b as BarDatum & { originalDate?: string };
    const originalDateA = itemA.originalDate || '';
    const originalDateB = itemB.originalDate || '';
    
    if (originalDateA && originalDateB) {
      const dateA = new Date(originalDateA.split('/').reverse().join('-'));
      const dateB = new Date(originalDateB.split('/').reverse().join('-'));
      return dateA.getTime() - dateB.getTime();
    }
    
    return 0;
  });

  return result;
}

const PAD_NUMBER = 2;
